import { useState, useEffect } from "react";
import { Accessibility, Plus, Minus, Eye, Type, Link2, RotateCcw, X } from "lucide-react";

const SETTINGS_KEY = "afford_accessibility";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({
    textSize: 0,
    grayscale: false,
    highContrast: false,
    negativeContrast: false,
    lightBg: false,
    underlineLinks: false,
    readableFont: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch {}
    }
  }, []);

  const applySettings = (s) => {
    const root = document.documentElement;

    // Text size - use percentage scaling on html element
    const baseSize = 100 + (s.textSize * 15);
    root.style.fontSize = `${baseSize}%`;

    // Filters on body
    const filters = [];
    if (s.grayscale) filters.push("grayscale(100%)");
    if (s.negativeContrast) filters.push("invert(100%)");
    if (s.highContrast) filters.push("contrast(1.5)");
    document.body.style.filter = filters.length ? filters.join(" ") : "";

    // Light background
    if (s.lightBg) {
      document.body.style.backgroundColor = "#FFFFFF";
      document.body.style.color = "#000000";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }

    // Underline links
    const style = document.getElementById("a11y-link-style");
    if (s.underlineLinks) {
      if (!style) {
        const el = document.createElement("style");
        el.id = "a11y-link-style";
        el.textContent = "a { text-decoration: underline !important; }";
        document.head.appendChild(el);
      }
    } else {
      if (style) style.remove();
    }

    // Readable font
    const fontStyle = document.getElementById("a11y-font-style");
    if (s.readableFont) {
      if (!fontStyle) {
        const el = document.createElement("style");
        el.id = "a11y-font-style";
        el.textContent = "* { font-family: Arial, Helvetica, sans-serif !important; letter-spacing: 0.03em !important; }";
        document.head.appendChild(el);
      }
    } else {
      if (fontStyle) fontStyle.remove();
    }

    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  };

  const update = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
  };

  const increaseText = () => {
    if (settings.textSize < 4) update("textSize", settings.textSize + 1);
  };

  const decreaseText = () => {
    if (settings.textSize > -2) update("textSize", settings.textSize - 1);
  };

  const toggle = (key) => update(key, !settings[key]);

  const resetAll = () => {
    const defaults = {
      textSize: 0, grayscale: false, highContrast: false,
      negativeContrast: false, lightBg: false, underlineLinks: false, readableFont: false,
    };
    setSettings(defaults);
    applySettings(defaults);
  };

  const hasActive = settings.textSize !== 0 || settings.grayscale || settings.highContrast ||
    settings.negativeContrast || settings.lightBg || settings.underlineLinks || settings.readableFont;

  return (
    <>
      {/* Widget Toggle Button */}
      <button onClick={() => setOpen(!open)} aria-label="Accessibility options"
        data-testid="accessibility-toggle"
        className={`fixed left-4 top-20 z-[60] w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 ${
          hasActive ? "bg-amber-500 text-white" : "bg-white text-slate-700 border border-slate-200"
        }`}>
        <Accessibility className="w-5 h-5" />
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed left-4 top-32 z-[60] w-64 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden"
          role="dialog" aria-label="Accessibility settings" data-testid="accessibility-panel">
          <div className="flex items-center justify-between p-3 bg-slate-50 border-b border-slate-200">
            <p className="text-sm font-semibold text-slate-800">Accessibility</p>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Close accessibility panel">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-3 space-y-1.5">
            {/* Text Size */}
            <div className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-700">Text Size</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={decreaseText} aria-label="Decrease text size"
                  className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-30"
                  disabled={settings.textSize <= -2} data-testid="a11y-decrease-text">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-xs font-mono w-6 text-center text-slate-600">{settings.textSize > 0 ? `+${settings.textSize}` : settings.textSize}</span>
                <button onClick={increaseText} aria-label="Increase text size"
                  className="w-7 h-7 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 disabled:opacity-30"
                  disabled={settings.textSize >= 4} data-testid="a11y-increase-text">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Toggle Options */}
            {[
              { key: "grayscale", label: "Grayscale", icon: Eye },
              { key: "highContrast", label: "High Contrast", icon: Eye },
              { key: "negativeContrast", label: "Negative Contrast", icon: Eye },
              { key: "lightBg", label: "Light Background", icon: Eye },
              { key: "underlineLinks", label: "Links Underline", icon: Link2 },
              { key: "readableFont", label: "Readable Font", icon: Type },
            ].map(opt => (
              <button key={opt.key} onClick={() => toggle(opt.key)}
                aria-label={`Toggle ${opt.label}`} data-testid={`a11y-${opt.key}`}
                className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                  settings[opt.key] ? "bg-emerald-50 text-emerald-800" : "hover:bg-slate-50 text-slate-700"
                }`}>
                <opt.icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-xs">{opt.label}</span>
                {settings[opt.key] && <span className="ml-auto text-[10px] font-bold text-emerald-600">ON</span>}
              </button>
            ))}

            {/* Reset */}
            {hasActive && (
              <button onClick={resetAll} data-testid="a11y-reset"
                className="w-full flex items-center justify-center gap-2 p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 mt-2">
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold">Reset All</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
